import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';

interface ContactFormProps {
  onClose?: () => void;
  userName?: string;
  userEmail?: string;
  onSuccess?: () => void;
}

export default function ContactForm({ onClose, userName = '', userEmail = '', onSuccess }: ContactFormProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: userName,
    email: userEmail,
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowConfirm(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirm(false);
    setLoading(true);
    setError(null);

    try {
      // ログインユーザー情報を取得
      const { data: { user } } = await supabase.auth.getUser();

      // お問い合わせをデータベースに保存
      const { data: insertData, error: insertError } = await supabase
        .from('contact_messages')
        .insert([{
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          user_id: user?.id || null,
          status: 'unread'
        }])
        .select();

      if (insertError) {
        console.error('データベース保存エラー:', insertError);
        throw insertError;
      }

      console.log('問い合わせが正常に保存されました:', insertData);

      // Edge Functionでメール送信
      const { error: emailError } = await supabase.functions.invoke('send-contact-email', {
        body: formData
      });

      if (emailError) {
        console.error('メール送信エラー:', emailError);
        // メール送信失敗してもデータベースには保存されているので成功扱い
      }

      setSuccess(true);
      setFormData({ name: userName, email: userEmail, subject: '', message: '' });

      // 3秒後に成功メッセージを非表示
      setTimeout(() => {
        setSuccess(false);
        onSuccess?.();
      }, 3000);

    } catch (err) {
      setError(err instanceof Error ? err.message : t('contact.sendFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
          <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
          <div>
            <p className="text-green-700 font-medium">{t('contact.sendComplete')}</p>
            <p className="text-green-600 text-sm">{t('contact.sendCompleteNote')}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            {t('contact.name')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder={t('contact.namePlaceholder')}
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            {t('contact.emailLabel')} <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="example@email.com"
          />
        </div>

        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
            {t('contact.subject')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="subject"
            required
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder={t('contact.subjectPlaceholder')}
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            {t('contact.message')} <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            required
            rows={6}
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            placeholder={t('contact.messagePlaceholder')}
          />
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                {t('common.sending')}
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                {t('common.send')}
              </>
            )}
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common.cancel')}
            </button>
          )}
        </div>
      </form>

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('contact.confirmSend')}</h3>

            <div className="space-y-3 mb-6">
              <div className="border-b pb-2">
                <p className="text-sm text-gray-600">{t('contact.name')}</p>
                <p className="font-medium text-gray-900">{formData.name}</p>
              </div>
              <div className="border-b pb-2">
                <p className="text-sm text-gray-600">{t('contact.emailLabel')}</p>
                <p className="font-medium text-gray-900">{formData.email}</p>
              </div>
              <div className="border-b pb-2">
                <p className="text-sm text-gray-600">{t('contact.subject')}</p>
                <p className="font-medium text-gray-900">{formData.subject}</p>
              </div>
              <div className="border-b pb-2">
                <p className="text-sm text-gray-600">{t('contact.message')}</p>
                <p className="font-medium text-gray-900 whitespace-pre-wrap">{formData.message}</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-6">{t('contact.confirmSendNote')}</p>

            <div className="flex gap-3">
              <button
                onClick={handleConfirmSubmit}
                className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                {t('contact.sendBtn')}
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
